"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { FaStar } from "react-icons/fa6";
import type { ClientLogo } from "@/types";

export interface LogoSliderProps {
  logos: ClientLogo[];
  showBadges?: boolean;
  showNames?: boolean;
  nameClassName?: string;
  mobileNameClassName?: string;
  itemClassName?: string;
  intervalMs?: number;
  durationSeconds?: number;
  className?: string;
  cardClassName?: string;
  imageClassName?: string;
  imageWidth?: number;
  imageHeight?: number;
  mobileCardClassName?: string;
  mobileImageClassName?: string;
}

export default function LogoSlider({
  logos,
  showBadges = false,
  showNames = false,
  nameClassName,
  mobileNameClassName,
  itemClassName,
  intervalMs = 4000,
  durationSeconds,
  className = "",
  cardClassName,
  imageClassName,
  imageWidth,
  imageHeight,
  mobileCardClassName,
  mobileImageClassName,
}: LogoSliderProps) {
  const track = [...logos, ...logos];

  // Prepare chunks of 6 for mobile 2x3 grid view
  const chunkSize = 6;
  const pages: ClientLogo[][] = [];
  for (let i = 0; i < logos.length; i += chunkSize) {
    const chunk = logos.slice(i, i + chunkSize);
    if (chunk.length < chunkSize) {
      const padded = [...chunk, ...logos.slice(0, chunkSize - chunk.length)];
      pages.push(padded);
    } else {
      pages.push(chunk);
    }
  }

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);

  useEffect(() => {
    if (pages.length <= 1) return;

    const interval = setInterval(() => {
      setIsTransitioning(true);
      setCurrentIndex((prev) => prev + 1);
    }, intervalMs);

    return () => clearInterval(interval);
  }, [pages.length, intervalMs]);

  const handleTransitionEnd = () => {
    if (currentIndex >= pages.length) {
      setIsTransitioning(false);
      setCurrentIndex(0);
    }
  };

  useEffect(() => {
    if (!isTransitioning) {
      const raf = requestAnimationFrame(() => {
        setIsTransitioning(true);
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [isTransitioning]);

  const displayPages = pages.length > 1 ? [...pages, pages[0]] : pages;
  const totalPages = displayPages.length;

  const renderBadge = (
    type?: "dealer" | "distributor" | "exclusive-distributor",
    size: "sm" | "md" = "sm"
  ) => {
    if (!showBadges || !type) return null;

    return (
      <div className="absolute top-3 left-3 z-10 flex items-center justify-center pointer-events-none">
        {type === "exclusive-distributor" ? (
          <FaStar
            className={
              size === "md"
                ? "text-[#9E2A7B] text-xs sm:text-base"
                : "text-[#9E2A7B] text-xs"
            }
            role="img"
            aria-label="Exclusive Distributor"
            title="Exclusive Distributor"
          />
        ) : type === "distributor" ? (
          <span
            role="img"
            className={
              size === "md"
                ? "inline-block w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-[#9E2A7B] shrink-0"
                : "inline-block w-2.5 h-2.5 rounded-full bg-[#9E2A7B] shrink-0"
            }
            aria-label="Distributor"
            title="Distributor"
          />
        ) : (
          <span
            role="img"
            className={
              size === "md"
                ? "inline-block w-2.5 h-2.5 sm:w-3 sm:h-3 bg-[#9E2A7B] shrink-0"
                : "inline-block w-2.5 h-2.5 bg-[#9E2A7B] shrink-0"
            }
            aria-label="Authorized Dealer"
            title="Authorized Dealer"
          />
        )}
      </div>
    );
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Mobile 6-card Grid Slider (< sm) */}
      <div className="block sm:hidden w-full px-2">
        <div className="w-full overflow-hidden">
          <div
            className="flex"
            style={{
              width: `${totalPages * 100}%`,
              transform: `translateX(-${(currentIndex * 100) / totalPages}%)`,
              transition: isTransitioning
                ? "transform 600ms cubic-bezier(0.4, 0, 0.2, 1)"
                : "none",
            }}
            onTransitionEnd={handleTransitionEnd}
          >
            {displayPages.map((page, pageIdx) => (
              <div
                key={`page-${pageIdx}`}
                style={{ width: `${100 / totalPages}%` }}
                className="shrink-0 px-2 grid grid-cols-2 gap-3.5 sm:gap-4"
              >
                {page.map((logo, idx) => {
                  const displayName = logo.name || logo.alt;

                  if (!showNames) {
                    return (
                      <div
                        key={`mobile-logo-${logo.id}-${pageIdx}-${idx}`}
                        className={`relative flex items-center justify-center backdrop-blur-2xl rounded-[10px] border border-gray-400/40 shrink-0 ${mobileCardClassName || "py-3 px-2 h-32"
                          }`}
                      >
                        {renderBadge(logo.type, "sm")}
                        <Image
                          src={logo.src}
                          alt={logo.alt}
                          width={imageWidth ? Math.round(imageWidth * 0.8) : 300}
                          height={imageHeight ? Math.round(imageHeight * 0.8) : 160}
                          className={
                            mobileImageClassName ||
                            imageClassName ||
                            "max-h-[52px] w-auto max-w-[85%] object-contain opacity-90 bg-transparent p-1 rounded"
                          }
                        />
                      </div>
                    );
                  }

                  return (
                    <div
                      key={`mobile-logo-${logo.id}-${pageIdx}-${idx}`}
                      className={`relative flex flex-col items-center justify-between backdrop-blur-2xl rounded-[10px] border border-gray-400/40 shrink-0 py-3 px-2 ${mobileCardClassName || "h-32"
                        }`}
                    >
                      {renderBadge(logo.type, "sm")}
                      <div className="w-full flex-1 min-h-0 flex items-center justify-center">
                        <Image
                          src={logo.src}
                          alt={logo.alt}
                          width={imageWidth ? Math.round(imageWidth * 0.8) : 300}
                          height={imageHeight ? Math.round(imageHeight * 0.8) : 160}
                          className={
                            mobileImageClassName ||
                            imageClassName ||
                            "max-h-[52px] w-auto max-w-[85%] object-contain opacity-90 bg-transparent p-1 rounded"
                          }
                        />
                      </div>
                      <div className="w-full h-8 flex items-center justify-center mt-1">
                        <span className={mobileNameClassName || "text-[11px] font-medium text-brand-cream/90 text-center leading-tight line-clamp-2 px-0.5"}>
                          {displayName}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Desktop / Tablet Marquee Slider (>= sm) */}
      <div className="hidden sm:block w-full overflow-hidden group">
        <div
          className="flex gap-5 w-max animate-marquee group-hover:[animation-play-state:paused]"
          style={{
            animationDuration: `${durationSeconds || Math.max(35, logos.length * 3)}s`,
          }}
        >
          {track.map((logo, idx) => {
            const displayName = logo.name || logo.alt;

            const cardNode = (
              <div
                className={`relative flex items-center justify-center backdrop-blur-2xl rounded-[10px] border border-gray-400/40 shrink-0 transition-transform duration-300 ${cardClassName || "p-4 sm:p-5 h-32 sm:h-36 w-[220px] sm:w-[260px]"
                  }`}
              >
                {renderBadge(logo.type, "md")}
                <Image
                  src={logo.src}
                  alt={logo.alt}
                  width={imageWidth || 380}
                  height={imageHeight || 200}
                  className={
                    imageClassName ||
                    "h-28 sm:h-32 w-auto max-w-[90%] object-contain opacity-90 hover:opacity-100 transition-all duration-300 bg-transparent p-0 scale-115 hover:scale-120"
                  }
                />
              </div>
            );

            if (!showNames) {
              return (
                <div
                  key={`desktop-logo-${logo.id}-${idx}`}
                  className={`relative flex items-center justify-center backdrop-blur-2xl rounded-[10px] border border-gray-400/40 shrink-0 transition-transform duration-300 ${cardClassName || "p-4 sm:p-5 h-32 sm:h-36 w-[220px] sm:w-[260px]"
                    }`}
                >
                  {renderBadge(logo.type, "md")}
                  <Image
                    src={logo.src}
                    alt={logo.alt}
                    width={imageWidth || 380}
                    height={imageHeight || 200}
                    className={
                      imageClassName ||
                      "h-28 sm:h-32 w-auto max-w-[90%] object-contain opacity-90 hover:opacity-100 transition-all duration-300 bg-transparent p-0 scale-115 hover:scale-120"
                    }
                  />
                </div>
              );
            }

            return (
              <div
                key={`desktop-item-${logo.id}-${idx}`}
                className={`flex flex-col items-center shrink-0 ${itemClassName || "w-[220px] sm:w-[260px]"}`}
              >
                {cardNode}
                {displayName && (
                  <div className="w-full mt-2.5 px-2 text-center min-h-[36px] flex items-start justify-center">
                    <p className={nameClassName || "text-base font-semibold text-brand-cream/80 text-center leading-snug line-clamp-2"}>
                      {displayName}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
